import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertTitle, AlertDescription,
	Button,
	Dialog, DialogContent, DialogFooter, DialogHeader,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useGetMeQuery, useListSubAccountsQuery, useUpdatePropertyMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'

const SELF_VALUE = '__self__'
const NONE_VALUE = '__none__'

interface AssignManagerDialogProps {
	open:           boolean
	onOpenChange:   (open: boolean) => void
	propertyId:     string
	currentManagerId?: string | null
}

export function AssignManagerDialog({ open, onOpenChange, propertyId, currentManagerId }: AssignManagerDialogProps) {
	const { t } = useTranslation()
	const { data: me } = useGetMeQuery()
	const { data: subAccountsData } = useListSubAccountsQuery({ status: 'ACTIVE' })
	const [updateProperty, { isLoading }] = useUpdatePropertyMutation()
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const subAccounts = subAccountsData ?? []

	const initialValue =
		!currentManagerId ? NONE_VALUE :
		me?.id === currentManagerId ? SELF_VALUE :
		currentManagerId
	const [value, setValue] = useState<string>(initialValue)

	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setValue(initialValue)
			 
			setErrorMsg(undefined)
		}
	}, [open, initialValue])

	async function handleConfirm() {
		const nextManagerId =
			value === NONE_VALUE ? null :
			value === SELF_VALUE ? (me?.id ?? null) :
			value
		try {
			setErrorMsg(undefined)
			await updateProperty({ id: propertyId, body: { managerId: nextManagerId ?? undefined } }).unwrap()
			onOpenChange(false)
		} catch (err) {
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col p-0 gap-0 overflow-visible rounded-(--radius-lg)"
				style={{ width: 460, maxWidth: '95vw' }}
			>
				<DialogHeader title={t('properties.card.assignManager.title')} />

				<div className="flex flex-col gap-3 px-6 py-4">
					{errorMsg !== undefined && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertTitle>{t('properties.card.assignManager.error')}</AlertTitle>
							{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
						</Alert>
					)}
					<label className="flex flex-col gap-2 text-sm text-(--color-foreground)">
						{t('properties.card.assignManager.label')}
						<Select value={value} onValueChange={setValue}>
							<SelectTrigger size="md" className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NONE_VALUE}>{t('properties.card.assignManager.none')}</SelectItem>
								<SelectItem value={SELF_VALUE}>
									{t('properties.card.assignManager.self', { name: me?.name ?? '' })}
								</SelectItem>
								{subAccounts.map((sa) => (
									<SelectItem key={sa.id} value={sa.id}>{sa.fullName}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</label>
				</div>

				<DialogFooter>
					<Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button type="button" onClick={() => void handleConfirm()} disabled={isLoading}>
						{t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
